import { ChangeEvent } from 'react';
import { X, Camera } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

interface PassportVerificationProps {
  passportNumber: string;
  onPassportNumberChange: (value: string) => void;
  onDocumentFrontUpload: (file: File) => void;
  onDocumentBackUpload: (file: File) => void;
  documentFrontPreview?: string;
  documentBackPreview?: string;
  documentFrontFile?: File;
  documentBackFile?: File;
  onRemoveDocumentFront?: () => void;
  onRemoveDocumentBack?: () => void;
  errors?: {
    passportNumber?: string;
    passportDocumentFront?: string;
    passportDocumentBack?: string;
  };
}

const PassportVerification = ({
  passportNumber,
  onPassportNumberChange,
  onDocumentFrontUpload,
  onDocumentBackUpload,
  documentFrontPreview,
  documentBackPreview,
  documentFrontFile,
  documentBackFile,
  onRemoveDocumentFront,
  onRemoveDocumentBack,
  errors,
}: PassportVerificationProps) => {
  const handlePassportNumberChange = (value: string) => {
    const cleanedValue = value
      .replace(/[^a-zA-Z0-9]/g, '')
      .toUpperCase()
      .slice(0, 9);
    onPassportNumberChange(cleanedValue);
  };

  const handleFrontDocumentChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onDocumentFrontUpload(file);
      e.target.value = '';
    }
  };

  const handleBackDocumentChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onDocumentBackUpload(file);
      e.target.value = '';
    }
  };

  const handleRemoveFrontDocument = () => {
    onRemoveDocumentFront?.();
  };

  const handleRemoveBackDocument = () => {
    onRemoveDocumentBack?.();
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="passportNumber">Passport Number</Label>
        <Input
          id="passportNumber"
          placeholder="A1234567"
          value={passportNumber}
          onChange={e => handlePassportNumberChange(e.target.value)}
          autoComplete="off"
          maxLength={9}
          className={errors?.passportNumber ? 'border-red-500' : ''}
        />
        {errors?.passportNumber && (
          <p className="text-sm text-destructive">{errors.passportNumber}</p>
        )}
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="passportDocumentFront">Passport - Front</Label>
          <div className="border-2 border-dashed border-border rounded-lg p-4">
            <Input
              id="passportDocumentFront"
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              className="hidden"
              onChange={handleFrontDocumentChange}
            />
            <label htmlFor="passportDocumentFront" className="cursor-pointer">
              <div className="flex flex-col items-center justify-center w-auto h-20 text-center">
                {documentFrontPreview ? (
                  <div className="relative inline-block">
                    <img
                      src={documentFrontPreview}
                      alt="Passport Front"
                      className="max-w-full max-h-20 object-contain rounded-lg"
                      onError={e => {
                        console.error('Image load error:', e);
                        e.currentTarget.style.display = 'none';
                      }}
                      onLoad={e => {
                        e.currentTarget.style.display = 'block';
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={e => {
                        e.preventDefault();
                        handleRemoveFrontDocument();
                      }}
                      className="absolute top-0 right-0 p-1"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Camera className="h-8 w-8 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {documentFrontFile
                        ? documentFrontFile.name
                        : 'Click to upload front side'}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      PNG, JPG, JPEG up to 10MB
                    </span>
                  </>
                )}
              </div>
            </label>
          </div>

          {errors?.passportDocumentFront && !documentFrontFile && (
            <p className="text-sm text-destructive">
              {errors.passportDocumentFront}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="passportDocumentBack">Passport - Back</Label>
          <div className="border-2 border-dashed border-border rounded-lg p-4">
            <Input
              id="passportDocumentBack"
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              className="hidden"
              onChange={handleBackDocumentChange}
            />
            <label htmlFor="passportDocumentBack" className="cursor-pointer">
              <div className="flex flex-col items-center justify-center w-auto h-20 text-center">
                {documentBackPreview ? (
                  <div className="relative inline-block">
                    <img
                      src={documentBackPreview}
                      alt="Passport Back"
                      className="max-w-full max-h-20 object-contain rounded-lg"
                      onError={e => {
                        console.error('Image load error:', e);
                        e.currentTarget.style.display = 'none';
                      }}
                      onLoad={e => {
                        e.currentTarget.style.display = 'block';
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={e => {
                        e.preventDefault();
                        handleRemoveBackDocument();
                      }}
                      className="absolute top-0 right-0 p-1"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Camera className="h-8 w-8 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {documentBackFile
                        ? documentBackFile.name
                        : 'Click to upload back side'}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      PNG, JPG, JPEG up to 10MB
                    </span>
                  </>
                )}
              </div>
            </label>
          </div>

          {errors?.passportDocumentBack && !documentBackFile && (
            <p className="text-sm text-destructive">
              {errors.passportDocumentBack}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PassportVerification;
